import {ButtonType} from '@mintdata/mint-components/components/button/ButtonType';
import {LinkTarget} from '@mintdata/mint-components/components/button/LinkTarget';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {Background} from '@mintdata/mint-kit/api/models/background/Background';
import {BackgroundType} from '@mintdata/mint-kit/api/models/background/BackgroundType';
import {BaseComponentModel} from '@mintdata/mint-kit/api/models/BaseComponentModel';
import {Border} from '@mintdata/mint-kit/api/models/Border';
import {BoxShadow} from '@mintdata/mint-kit/api/models/BoxShadow';
import {ColorWithVisibility} from '@mintdata/mint-kit/api/models/ColorWithVisibility';
import {ComponentState} from '@mintdata/mint-kit/api/models/componentState/ComponentState';
import {Font} from '@mintdata/mint-kit/api/models/Font';
import {IconId, IconIds} from '@mintdata/mint-kit/api/models/IconId';
import {PositiveOffsets} from '@mintdata/mint-kit/api/models/PositiveOffsets';
import {TextAlign} from '@mintdata/mint-kit/api/models/TextAlign';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {BindingConfig, BindingIconPlace} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {FieldPath} from '@mintdata/mint-kit/api/properties/BindPath';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {ABooleanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/ABooleanConfig';
import {AStringEnumConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AEnumConfig';
import {AStringConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/AStringConfig';
import {BeanConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/BeanConfig';
import {FontConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/FontConfig';
import {PositiveOffsetsConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/PositiveOffsetsConfig';
import {TextAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextAlignConfig';
import {TextVerticalAlignConfig} from '@mintdata/mint-kit/impl/properties/typeConfigs/TextVerticalAlignConfig';

export class ButtonModel extends BaseComponentModel
{
    @Property<AString>({
        title: 'Caption',
        tip: `Caption`,
        isRequired: false,
        typeConfig: AStringConfig,
        typeCtor: AString,
        binding: BindingConfig.with('@caption[0]')
    })
    public caption?:AString;

    @Property<ABoolean>({
        title: 'Caption Visibility',
        tip: `Caption Visibility`,
        isRequired: false,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean,
        binding: BindingConfig.with('@visibility[0]', BindingIconPlace.RightTop)
    })
    public captionVisibility?:ABoolean;

    @Property<Font>({
        title: 'Font',
        tip: 'Font',
        isRequired: false,
        typeConfig: FontConfig,
        typeCtor: Font
    })
    public font?:Font;

    @Property<IconId>({
        title: 'Icon Right',
        tip: `Icon Right`,
        isRequired: false,
        typeConfig: AStringEnumConfig,
        typeCtor: IconId
    })
    public iconRight?:IconId;

    @Property<IconId>({
        title: 'Icon Left',
        tip: `Icon Left`,
        isRequired: false,
        typeConfig: AStringEnumConfig,
        typeCtor: IconId
    })
    public iconLeft?:IconId;

    @Property<ABoolean>({
        title: 'Is Disabled',
        tip: `Is Disabled`,
        isRequired: false,
        typeConfig: ABooleanConfig,
        typeCtor: ABoolean,
        binding: BindingConfig.with('@disabled[0]')
    })
    public isDisabled?:ABoolean;

    @Property<ButtonType>({
        title: 'Type',
        tip: `Type`,
        isRequired: false,
        typeConfig: AStringEnumConfig,
        typeCtor: ButtonType
    })
    type:ButtonType;

    @Property<TextAlign>({
        title: 'Text Align',
        tip: 'Text Align',
        isRequired: false,
        typeConfig: TextAlignConfig,
        typeCtor: TextAlign
    })
    public textAlign:TextAlign;

    @Property<TextVerticalAlign>({
        title: 'Vertical Align',
        tip: 'Vertical Align',
        isRequired: false,
        typeConfig: TextVerticalAlignConfig,
        typeCtor: TextVerticalAlign
    })
    public textVerticalAlign:TextVerticalAlign;

    @Property<Background>({
        title: 'Background',
        tip: 'Background',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Background
    })
    background:Background;

    @Property<Border>({
        title: 'Border',
        tip: 'Border',
        isRequired: true,
        typeConfig: BeanConfig,
        typeCtor: Border
    })
    public border:Border;

    @Property<AString>({
        title: 'Url',
        tip: `Url`,
        isRequired: false,
        typeConfig: AStringConfig,
        typeCtor: AString
    })
    public url?:AString;

    @Property<LinkTarget>({
        title: 'Is New Window',
        tip: `Is New Window`,
        isRequired: false,
        typeConfig: AStringEnumConfig,
        typeCtor: LinkTarget
    })
    public isNewWindow?:LinkTarget;

    @Property<BoxShadow>({
        title: 'Shadows',
        tip: 'Shadows',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: BoxShadow
    })
    public boxShadow?:BoxShadow = BoxShadow.default();

    @Property<ComponentState>({
        title: 'Hover State',
        tip: 'Hover State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: ComponentState
    })
    public hoverState?:ComponentState;

    @Property<ComponentState>({
        title: 'Active State',
        tip: 'Active State',
        isRequired: false,
        typeConfig: BeanConfig,
        typeCtor: ComponentState
    })
    public activeState?:ComponentState;

    @Property<PositiveOffsets>({
        title: 'Padding',
        tip: 'Padding',
        category: PropertyCategory.Layout,
        isRequired: false,
        typeConfig: PositiveOffsetsConfig,
        typeCtor: PositiveOffsets
    })
    public padding?:PositiveOffsets = PositiveOffsets.fromEqualPixels(0);

    public constructor(caption?:AString,
                       captionVisibility?:ABoolean,
                       font?:Font,
                       iconRight?:IconId,
                       iconLeft?:IconId,
                       isDisabled?:ABoolean,
                       type?:ButtonType,
                       textAlign?:TextAlign,
                       textVerticalAlign?:TextVerticalAlign,
                       background?:Background,
                       border?:Border,
                       url?:AString,
                       isNewWindow?:LinkTarget,
                       boxShadow?:BoxShadow,
                       hoverState?:ComponentState,
                       activeState?:ComponentState,
                       padding?:PositiveOffsets)
    {
        super();

        this.caption = caption;
        this.captionVisibility = captionVisibility;
        this.font = font;
        this.iconRight = iconRight;
        this.iconLeft = iconLeft;
        this.isDisabled = isDisabled;
        this.type = type;
        this.textAlign = textAlign;
        this.textVerticalAlign = textVerticalAlign;
        this.background = background;
        this.border = border;
        this.url = url;
        this.isNewWindow = isNewWindow;
        this.boxShadow = boxShadow;
        this.hoverState = hoverState;
        this.activeState = activeState;
    }

    public static default():ButtonModel
    {
        return new ButtonModel(
            AString.fromString('Button'),
            ABoolean.true(),
            Font.default(),
            IconId.fromString(IconIds.BLANK),
            IconId.fromString(IconIds.BLANK),
            ABoolean.false(),
            ButtonType.button(),
            TextAlign.center(),
            TextVerticalAlign.middle(),
            new Background(
                BackgroundType.gradient(),
                ColorWithVisibility.fromHexAlpha('#f5f8fa', 1),
                ColorWithVisibility.fromHexAlpha('#ffffff', 1),
                ColorWithVisibility.fromHexAlpha('#f5f8fa', 1),
                ANumber.fromNumber(90)
            ),
            Border.defaultForInput(),
            AString.fromString(''),
            LinkTarget.self(),
            BoxShadow.default(),
            ButtonModel._getHoverState(),
            ButtonModel._getActiveState()
        );
    }

    private static _getHoverState():ComponentState
    {
        const hoverState:ComponentState = new ComponentState();

        hoverState.isEnabled = ABoolean.true();
        hoverState.font = Font.default();
        hoverState.background = new Background(
            BackgroundType.gradient(),
            ColorWithVisibility.fromHexAlpha('#f5f8fa', 1),
            ColorWithVisibility.fromHexAlpha('#ffffff', 1),
            ColorWithVisibility.fromHexAlpha('#e4ecf2', 1),
            ANumber.fromNumber(90)
        );
        hoverState.border = Border.defaultForInput();
        hoverState.boxShadow = BoxShadow.default();

        return hoverState;
    }

    private static _getActiveState():ComponentState
    {
        const activeState:ComponentState = new ComponentState();

        activeState.isEnabled = ABoolean.true();
        activeState.font = Font.default();
        activeState.background = new Background(
            BackgroundType.solid(),
            ColorWithVisibility.fromHexAlpha('#d3e0e9', 1),
            ColorWithVisibility.fromHexAlpha('#ffffff', 1),
            ColorWithVisibility.fromHexAlpha('#e4ecf2', 1),
            ANumber.fromNumber(90)
        );
        activeState.border = Border.defaultForInput();
        activeState.boxShadow = BoxShadow.default();

        return activeState;
    }

    public static exampleFrame():IFrame
    {
        return exampleFrame;
    }

    public static normalToHoverStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['font', 'hoverState.font'],
            ['background', 'hoverState.background'],
            ['border', 'hoverState.border'],
            ['boxShadow', 'hoverState.boxShadow']
        ]);
    }

    public static normalToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['font', 'activeState.font'],
            ['background', 'activeState.background'],
            ['border', 'activeState.border'],
            ['boxShadow', 'activeState.boxShadow']
        ]);
    }

    public static hoverToActiveStatePathsMap():Map<FieldPath, FieldPath>
    {
        return new Map([
            ['hoverState.font', 'activeState.font'],
            ['hoverState.background', 'activeState.background'],
            ['hoverState.border', 'activeState.border'],
            ['hoverState.boxShadow', 'activeState.boxShadow']
        ]);
    }
}

const exampleFrame:IFrame = new AFrame(
    [
        new FrameField('caption', Type.String),
        new FrameField('visibility', Type.Boolean),
        new FrameField('disabled', Type.Boolean),
        new FrameField('url', Type.String)
    ],
    [
        [
            AString.fromString('Button'),
            ABoolean.true(),
            ABoolean.false(),
            AString.fromString('')
        ]
    ]
);
